import { createFileRoute, linkOptions } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  CompaniesTypeFilter,
  type CompanyFilters,
  type CompanyResponse,
  type CompanySortingState,
  type CoveragesGroupInput,
  companySortingColumns,
} from 'types'
import z from 'zod'

import {
  queryCompanyCount,
  queryCompanyList,
} from '@api/company/company.queries'

import StreamingExportDialog from '@/components/export/streaming-export-dialog'
import { ExportType } from '@/components/export/streaming-export-dialog.types'
import CompaniesTypeFilterSelect from '@/components/filters/companies-type-filter-select'
import InsuranceCompanySelect from '@/components/insurance-company-select'
import LastImportSubtitle from '@/components/last-import-subtitle'
import PortfolioSelect from '@/components/portfolio-select'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import { CompaniesColumn } from '@/components/tables/companies/companies-columns.enum'
import CompaniesTable, {
  useDebouncedCompanyFilters,
} from '@/components/tables/companies/companies-table'
import { numberFilterSchema } from '@/lib/filter'
import { cursorPaginationSchema, sortingSchema } from '@/lib/table'
import { insuranceCompanyFilterAtom } from '@/stores/insurance-company-filter.atom'
import { storePageSearch } from '@/stores/page-search.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import { queryImportRunsLatest } from '@api/import-run/import-run.queries'
import { useQuery } from '@tanstack/react-query'
import SectionHeader from '../../-components/section-layout/section-header'
import CompaniesStats from './-components/companies-stats'

const searchSchema = z.object({
  pagination: cursorPaginationSchema,
  sorting: sortingSchema(companySortingColumns),
  filters: z
    .object({
      search: z.string().optional(),
      types: z.enum(CompaniesTypeFilter).optional(),
      legalName: z.string().optional(),
      vatNumber: z.string().optional(),
      groupName: z.string().optional(),
      countryCode: z.string().optional(),
      rating: numberFilterSchema.optional(),
    })
    .optional(),
})

export const Route = createFileRoute('/app/data/companies/')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps: { pagination, sorting } }) => {
    queryClient.prefetchQuery(
      queryCompanyList({
        ...pagination,
        sorting,
      }),
    )
    queryClient.prefetchQuery(queryCompanyCount())
  },
  beforeLoad: ({ search, cause, location }) => {
    storePageSearch(
      Route.fullPath,
      { to: Route.fullPath, search },
      location,
      cause,
    )
  },
})

const rowLink = (row: CompanyResponse) =>
  linkOptions({
    to: `/app/data/companies/$uuid/${row.stats.isInsured ? 'insured' : 'debtor'}`,
    params: { uuid: row.uuid },
  })

const excludedColumns: CompaniesColumn[] = [
  CompaniesColumn.InsurerIds,
  CompaniesColumn.PoliciesCount,
]

function RouteComponent() {
  const search = Route.useSearch()
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const portfolioFilter = useAtomValue(portfolioFilterAtom)
  const insuranceCompanyFilter = useAtomValue(insuranceCompanyFilterAtom)
  const { data: latestImportRuns } = useQuery(queryImportRunsLatest())

  const filters: CompanyFilters = {
    ...search.filters,
    portfolioUserUuid: portfolioFilter,
    insuranceCompany: insuranceCompanyFilter,
  }

  const debouncedFilters = useDebouncedCompanyFilters(filters)

  const coveragesGroupInput: CoveragesGroupInput = {
    items: [{ criteria: 'none' }],
    filters: {
      portfolioUserUuid: debouncedFilters.portfolioUserUuid,
      insuranceCompanies: debouncedFilters.insuranceCompany
        ? [debouncedFilters.insuranceCompany]
        : undefined,
      groupUuids: debouncedFilters.groupUuid
        ? [debouncedFilters.groupUuid]
        : undefined,
      company: debouncedFilters,
    },
  }

  const setFilters = (updater: (filter?: CompanyFilters) => CompanyFilters) =>
    navigate({
      search: (previous) => ({
        ...previous,
        filters: updater(search.filters),
      }),
    })

  const setPagination = (
    updater: (pagination: CursorPaginationState) => CursorPaginationState,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        pagination: updater(previous.pagination),
      }),
    })

  const setSorting = (
    updater: (sorting?: CompanySortingState) => CompanySortingState,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        sorting: updater(previous.sorting),
      }),
    })

  const handleTypeChange = (types?: CompaniesTypeFilter) =>
    setFilters((previous) => ({ ...previous, types }))

  const handleSearchChange = (search: string) =>
    setFilters((previous) => ({ ...previous, search }))

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader
        isSecondary
        title={t('data.companies.title')}
        subtitle={
          <LastImportSubtitle
            data={latestImportRuns}
            activeInsurer={insuranceCompanyFilter}
          />
        }
      >
        <div className="flex items-center gap-2">
          <CompaniesTypeFilterSelect
            value={filters.types}
            onChange={handleTypeChange}
          />
          <SearchBar
            tabIndex={1}
            className="max-w-62.75"
            text={filters.search}
            setText={handleSearchChange}
          />
          <InsuranceCompanySelect />
          <PortfolioSelect />
          <StreamingExportDialog
            type={ExportType.Companies}
            filters={filters}
          />
        </div>
      </SectionHeader>
      <div className="flex h-full flex-col gap-8 overflow-hidden p-2">
        <CompaniesStats input={coveragesGroupInput} />
        <CompaniesTable
          filters={debouncedFilters}
          setFilters={setFilters}
          pagination={search.pagination}
          setPagination={setPagination}
          sorting={search.sorting}
          setSorting={setSorting}
          rowLink={rowLink}
          excludedColumns={excludedColumns}
        />
      </div>
    </div>
  )
}
